import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '@/lib/api';

type Recipient = {
  user_id: number;
  phone: string;
  user_name: string | null;
  user_active: boolean;
};

type Config = {
  active: boolean;
  endpoint: string | null;
  api_key_masked: string | null;
  has_api_key: boolean;
  recipients: Recipient[];
  sections: string[];
  horizon_days: number;
  send_time: string;
  max_items_per_section: number;
  send_when_empty: boolean;
  last_sent_date: string | null;
  last_result: string | null;
};

type Meta = {
  available_sections: Array<{ key: string; label: string }>;
  available_users: Array<{ id: number; name: string; email: string }>;
  usable: boolean;
  missing: string[];
  schedule: string;
  server_time: string;
  server_date: string;
  server_timezone: string;
  sent_today: boolean;
  next_check: string;
  status_explanation: string;
  scheduler_alive: boolean;
  scheduler_last_tick: string | null;
  scheduler_minutes_ago: number | null;
};

type Preview = {
  user_id: number;
  user_name: string | null;
  text: string | null;
  is_empty: boolean;
  message: string | null;
};

/**
 * Resumo diário por WhatsApp — RF-ALE-05.
 *
 * ================================================================
 * A TELA RESPONDE TRÊS PERGUNTAS, NESTA ORDEM
 * ================================================================
 *
 *   1. Está funcionando?      o aviso no topo, e a data do último envio
 *   2. Para onde vai?         API, chave e destinatários
 *   3. O que vai chegar?      os blocos, e a PRÉVIA do texto
 *
 * A prévia é a parte que costuma faltar em telas assim. Sem ela, a única forma
 * de saber o que a mensagem diz é esperar o dia seguinte — ou disparar um envio
 * de verdade no celular de alguém para cada ajuste de configuração.
 *
 * ================================================================
 * A CHAVE NUNCA É PREENCHIDA DE VOLTA
 * ================================================================
 *
 * O servidor devolve `••••4yS`, nunca a chave. O campo fica vazio com esse
 * texto como placeholder: digitar troca, deixar em branco mantém. É por isso
 * que o formulário só envia `api_key` quando ela foi realmente digitada —
 * mandar string vazia apagaria a chave a cada "salvar".
 */
export function WhatsAppPage() {
  const queryClient = useQueryClient();

  const config = useQuery({
    queryKey: ['whatsapp-digest'],
    queryFn: () => api.get<{ data: Config; meta: Meta }>('/whatsapp-digest'),
  });

  const [form, setForm] = useState<Config | null>(null);
  const [novaChave, setNovaChave] = useState('');
  const [testePhone, setTestePhone] = useState('');
  const [testeMensagem, setTesteMensagem] = useState('');
  const [previa, setPrevia] = useState<Preview[] | null>(null);

  // O formulário nasce do servidor e só é semeado uma vez: recarregar a cada
  // refetch descartaria o que a pessoa está digitando.
  useEffect(() => {
    if (config.data && form === null) setForm(config.data.data);
  }, [config.data, form]);

  const salvar = useMutation({
    mutationFn: () => {
      const f = form!;

      return api.put<{ data: Config; meta: Meta }>('/whatsapp-digest', {
        active: f.active,
        endpoint: f.endpoint || null,
        // Só vai quando digitada. Ausente = mantém a que está guardada.
        ...(novaChave.trim() !== '' ? { api_key: novaChave.trim() } : {}),
        recipients: f.recipients.map((r) => ({ user_id: r.user_id, phone: r.phone })),
        sections: f.sections,
        horizon_days: f.horizon_days,
        send_time: f.send_time,
        max_items_per_section: f.max_items_per_section,
        send_when_empty: f.send_when_empty,
      });
    },
    onSuccess: (res) => {
      setForm(res.data);
      setNovaChave('');
      queryClient.setQueryData(['whatsapp-digest'], res);
      window.alert('Configuração salva.');
    },
  });

  const verPrevia = useMutation({
    mutationFn: () => api.post<{ data: Preview[] }>('/whatsapp-digest/preview'),
    onSuccess: (res) => setPrevia(res.data),
  });

  const enviarTeste = useMutation({
    mutationFn: () =>
      api.post<{ meta: { note: string } }>('/whatsapp-digest/test', {
        phone: testePhone,
        message: testeMensagem || null,
      }),
    onSuccess: (res) => window.alert(res.meta.note),
    onError: (e) =>
      window.alert(e instanceof ApiError ? e.message : 'Não foi possível enviar o teste.'),
  });

  const enviarAgora = useMutation({
    mutationFn: () =>
      api.post<{ data: { sent: number; failed: number; skipped: number; details: Array<{ user_id: number; status: string; message: string | null }> }; meta: { note: string } }>(
        '/whatsapp-digest/send-now',
      ),
    onSuccess: (res) => {
      const falhas = res.data.details
        .filter((d) => d.message !== null)
        .map((d) => `· ${d.message}`)
        .join('\n');

      window.alert(falhas ? `${res.meta.note}\n\n${falhas}` : res.meta.note);
    },
    onError: (e) =>
      window.alert(e instanceof ApiError ? e.message : 'Não foi possível enviar o resumo.'),
  });

  if (config.isPending || form === null) {
    return <div className="mx-auto max-w-4xl text-sm text-slate-500">Carregando…</div>;
  }

  if (config.isError) {
    return (
      <div className="mx-auto max-w-4xl text-sm text-rose-700 dark:text-rose-400">
        Não foi possível carregar a configuração.
      </div>
    );
  }

  const meta = config.data.meta;
  const erro = salvar.error instanceof ApiError ? salvar.error : null;

  function set<K extends keyof Config>(campo: K, valor: Config[K]) {
    setForm((f) => (f === null ? f : { ...f, [campo]: valor }));
  }

  function alternarSecao(chave: string) {
    setForm((f) =>
      f === null
        ? f
        : {
            ...f,
            sections: f.sections.includes(chave)
              ? f.sections.filter((s) => s !== chave)
              : [...f.sections, chave],
          },
    );
  }

  return (
    <div className="mx-auto max-w-4xl">
      <div>
        <h1 className="text-xl font-semibold tracking-tight">Resumo diário no WhatsApp</h1>
        <p className="text-sm text-slate-500 dark:text-slate-400">{meta.schedule}</p>
      </div>

      {/* ================================================================
          ESTÁ FUNCIONANDO? — a primeira pergunta, e a mais fácil de errar.

          Configuração ativa e incompleta é o pior estado possível: a pessoa
          acha que configurou e fica esperando uma mensagem que nunca sai,
          sem nenhum erro em lugar nenhum.
          ================================================================ */}
      {form.active && !meta.usable && (
        <div className="mt-4 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-900/60 dark:bg-amber-950/40 dark:text-amber-200">
          <p className="font-medium">O envio está ligado, mas ainda não vai acontecer.</p>
          <p className="mt-1">Falta {meta.missing.join(', ')}.</p>
        </div>
      )}

      {/* ================================================================
          "POR QUE AINDA NÃO CHEGOU?"

          Três comportamentos corretos produzem juntos um silêncio que parece
          defeito: o agendador confere a cada 15 min, o resumo sai uma vez por
          dia, e o relógio que vale é o do servidor. Cada um é invisível de
          fora. O bloco diz qual deles está em jogo.
          ================================================================ */}
      {/* ================================================================
          O AGENDADOR PARADO É VERMELHO, E VEM ANTES DE TUDO.

          "Não chegou" tem duas causas indistinguíveis de fora: o horário ainda
          não chegou (inofensivo) ou o serviço do agendador não está rodando —
          e aí nada acontece nunca, nem backup, nem alertas. Pintar as duas de
          verde faria a tela prometer uma mensagem que não vai sair.
          ================================================================ */}
      {form.active && meta.usable && !meta.scheduler_alive && (
        <div className="mt-4 rounded-xl border border-rose-300 bg-rose-50 px-4 py-3 text-sm text-rose-900 dark:border-rose-900/60 dark:bg-rose-950/40 dark:text-rose-200">
          <p className="font-medium">O agendador não está rodando.</p>
          <p className="mt-1">{meta.status_explanation}</p>
          <p className="mt-1.5 text-xs opacity-80">
            Enquanto isso, “Enviar agora” continua funcionando — ele não depende do agendador.
          </p>
        </div>
      )}

      {form.active && meta.usable && meta.scheduler_alive && (
        <div className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900 dark:border-emerald-900/60 dark:bg-emerald-950/40 dark:text-emerald-200">
          <p className="font-medium">{meta.status_explanation}</p>
          <p className="mt-1 text-emerald-800/80 dark:text-emerald-300/80">
            {form.last_result ?? 'Nenhum envio ainda.'}
          </p>
          {meta.scheduler_last_tick !== null && (
            <p className="mt-1 text-xs text-emerald-800/70 dark:text-emerald-300/70">
              Agendador ativo — última verificação às {meta.scheduler_last_tick}.
            </p>
          )}
        </div>
      )}

      {erro && (
        <div
          role="alert"
          className="mt-4 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800 dark:border-rose-900/60 dark:bg-rose-950/40 dark:text-rose-300"
        >
          <p>{erro.message}</p>
          {erro.errors && (
            <ul className="mt-1 list-inside list-disc">
              {Object.entries(erro.errors).map(([campo, msgs]) => (
                <li key={campo}>{msgs[0]}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* --- Conexão ------------------------------------------------- */}
      <Section title="Conexão com a API">
        <p className="text-xs text-slate-500 dark:text-slate-400">
          Os contatos devem estar no formato internacional, sem o +.
        </p>

        <Field label="URL da API">
          <input
            value={form.endpoint ?? ''}
            onChange={(e) => set('endpoint', e.target.value)}
            placeholder="https://backend.exemplo.com.br/api/messages/send"
            className="input w-full"
          />
        </Field>

        <Field label="API Key">
          <input
            type="password"
            value={novaChave}
            onChange={(e) => setNovaChave(e.target.value)}
            placeholder={form.api_key_masked ?? 'Cole a chave'}
            autoComplete="new-password"
            className="input w-full"
          />
          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
            {form.has_api_key
              ? `Guardada e criptografada (${form.api_key_masked}). Deixe em branco para manter.`
              : 'Ainda não há chave guardada.'}
          </p>
        </Field>

        <Radio
          label="Ativo"
          value={form.active}
          onChange={(v) => set('active', v)}
        />
      </Section>

      {/* --- Destinatários ------------------------------------------- */}
      <Section title="Quem recebe">
        {/* ================================================================
            POR QUE O DESTINATÁRIO É UM USUÁRIO, E NÃO SÓ UM NÚMERO
            ================================================================

            O resumo carrega descrição, valor e vencimento. Ele passa pelo
            mesmo isolamento por entidade da tela: cada mensagem é montada com
            o acesso do usuário escolhido. Fosse só um telefone, o WhatsApp
            seria a porta dos fundos do controle de acesso.
            ================================================================ */}
        <p className="text-xs text-slate-500 dark:text-slate-400">
          Cada pessoa recebe o resumo das entidades a que ela tem acesso — o mesmo recorte que ela
          vê nas telas.
        </p>

        {form.recipients.length === 0 && (
          <p className="mt-3 rounded-lg bg-slate-50 px-3.5 py-2.5 text-sm text-slate-600 dark:bg-slate-800/50 dark:text-slate-300">
            Nenhum destinatário. Sem pelo menos um, nada é enviado.
          </p>
        )}

        <div className="mt-3 space-y-2">
          {form.recipients.map((r, i) => (
            <div key={i} className="flex flex-wrap items-center gap-2">
              <select
                value={r.user_id}
                onChange={(e) => {
                  const id = Number(e.target.value);
                  const escolhido = meta.available_users.find((u) => u.id === id);

                  set('recipients', form.recipients.map((item, j) =>
                    j === i
                      ? { ...item, user_id: id, user_name: escolhido?.name ?? null, user_active: true }
                      : item,
                  ));
                }}
                className="input min-w-[12rem] flex-1"
              >
                {meta.available_users.map((u) => (
                  <option key={u.id} value={u.id}>{u.name}</option>
                ))}
              </select>

              <input
                value={r.phone}
                onChange={(e) =>
                  set('recipients', form.recipients.map((item, j) =>
                    j === i ? { ...item, phone: e.target.value } : item,
                  ))
                }
                placeholder="5511999999999"
                inputMode="numeric"
                className="input min-w-[10rem] flex-1"
              />

              <button
                type="button"
                onClick={() => set('recipients', form.recipients.filter((_, j) => j !== i))}
                className="rounded-lg border border-slate-300 px-3 py-2 text-sm dark:border-slate-700"
              >
                Remover
              </button>

              {!r.user_active && r.user_name !== null && (
                <span className="w-full text-xs text-amber-700 dark:text-amber-400">
                  {r.user_name} está inativo — não vai receber.
                </span>
              )}
            </div>
          ))}
        </div>

        <button
          type="button"
          disabled={meta.available_users.length === 0}
          onClick={() => {
            const primeiro = meta.available_users[0];
            if (!primeiro) return;

            set('recipients', [
              ...form.recipients,
              { user_id: primeiro.id, phone: '', user_name: primeiro.name, user_active: true },
            ]);
          }}
          className="mt-3 rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium dark:border-slate-700"
        >
          Adicionar destinatário
        </button>
      </Section>

      {/* --- Conteúdo ------------------------------------------------ */}
      <Section title="O que chega na mensagem">
        <p className="text-xs text-slate-500 dark:text-slate-400">
          Os blocos aparecem na ordem da urgência: o que já venceu vem antes do que vence hoje, que
          vem antes do que vai vencer.
        </p>

        <div className="mt-3 space-y-2">
          {meta.available_sections.map((s) => (
            <label key={s.key} className="flex cursor-pointer items-start gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.sections.includes(s.key)}
                onChange={() => alternarSecao(s.key)}
                className="mt-0.5"
              />
              <span>{s.label}</span>
            </label>
          ))}
        </div>

        <div className="mt-4 grid gap-3 sm:grid-cols-3">
          <Field label="Horário do envio">
            <input
              type="time"
              value={form.send_time}
              onChange={(e) => set('send_time', e.target.value)}
              className="input w-full"
            />
            {/* O horário é comparado com o relógio do SERVIDOR, nunca com o
                deste computador. Sem esse número aqui, um servidor em outro
                fuso produz uma espera que ninguém consegue explicar. */}
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
              Agora no servidor: <strong className="tabular-nums">{meta.server_time}</strong>{' '}
              de {meta.server_date}. Próxima verificação às{' '}
              <span className="tabular-nums">{meta.next_check}</span>.
            </p>
          </Field>

          <Field label="Próximos dias">
            <input
              type="number"
              min={1}
              max={90}
              value={form.horizon_days}
              onChange={(e) => set('horizon_days', Number(e.target.value))}
              className="input w-full"
            />
          </Field>

          <Field label="Itens por bloco">
            <input
              type="number"
              min={0}
              max={500}
              value={form.max_items_per_section}
              onChange={(e) => set('max_items_per_section', Number(e.target.value))}
              className="input w-full"
            />
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
              {form.max_items_per_section === 0
                ? 'Envia todas as linhas. Se a mensagem ficar longa demais, ela é dividida em '
                  + 'partes numeradas — nada é omitido.'
                : `Mostra ${form.max_items_per_section} por bloco e resume o resto numa linha. `
                  + 'Use 0 para enviar todas.'}
            </p>
          </Field>
        </div>

        <label className="mt-3 flex cursor-pointer items-start gap-2 text-sm">
          <input
            type="checkbox"
            checked={form.send_when_empty}
            onChange={(e) => set('send_when_empty', e.target.checked)}
            className="mt-0.5"
          />
          <span>
            Enviar mesmo quando não houver nada
            <span className="block text-xs text-slate-500 dark:text-slate-400">
              Um “sem novidades” diário ensina a ignorar a notificação — e no dia em que houver
              algo, você não abre.
            </span>
          </span>
        </label>
      </Section>

      <div className="mt-5 flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => salvar.mutate()}
          disabled={salvar.isPending}
          className="rounded-lg bg-emerald-600 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-emerald-700 disabled:opacity-50"
        >
          {salvar.isPending ? 'Salvando…' : 'Salvar configuração'}
        </button>

        <button
          type="button"
          onClick={() => verPrevia.mutate()}
          disabled={verPrevia.isPending}
          className="rounded-lg border border-slate-300 px-4 py-2.5 text-sm font-medium dark:border-slate-700"
        >
          {verPrevia.isPending ? 'Montando…' : 'Ver prévia'}
        </button>

        <button
          type="button"
          onClick={() => {
            if (window.confirm('Enviar o resumo agora para todos os destinatários?')) {
              enviarAgora.mutate();
            }
          }}
          disabled={enviarAgora.isPending || !meta.usable}
          className="rounded-lg border border-slate-300 px-4 py-2.5 text-sm font-medium disabled:opacity-50 dark:border-slate-700"
        >
          {enviarAgora.isPending ? 'Enviando…' : 'Enviar agora'}
        </button>
      </div>

      {/* ================================================================
          A PRÉVIA — o que exatamente vai chegar no celular.

          Sem ela, a única forma de conferir o texto é esperar o dia seguinte,
          ou disparar um envio real a cada ajuste. `white-space: pre-wrap`
          preserva as quebras: é assim que a mensagem chega.
          ================================================================ */}
      {previa !== null && (
        <Section title="Prévia">
          {previa.length === 0 && (
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Nenhum destinatário configurado.
            </p>
          )}

          {previa.map((p) => (
            <div key={p.user_id} className="mt-3">
              <p className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
                {p.user_name ?? `Usuário ${p.user_id}`}
              </p>

              {p.message !== null && (
                <p className="mt-1 text-xs text-amber-700 dark:text-amber-400">{p.message}</p>
              )}

              {p.text !== null && (
                <pre className="mt-1 whitespace-pre-wrap rounded-lg bg-slate-50 px-3.5 py-3 font-sans text-sm text-slate-800 dark:bg-slate-800/50 dark:text-slate-200">
                  {p.text}
                </pre>
              )}
            </div>
          ))}
        </Section>
      )}

      {/* --- Teste ---------------------------------------------------- */}
      <Section title="Testar a configuração">
        <p className="text-xs text-slate-500 dark:text-slate-400">
          Manda uma mensagem avulsa, e não o resumo. Serve para responder uma pergunta só: a URL e a
          chave estão certas?
        </p>

        <div className="mt-3 space-y-3">
          <Field label="Telefone">
            <input
              value={testePhone}
              onChange={(e) => setTestePhone(e.target.value)}
              placeholder="5511999999999"
              inputMode="numeric"
              className="input w-full"
            />
          </Field>

          <Field label="Mensagem de teste">
            <textarea
              value={testeMensagem}
              onChange={(e) => setTesteMensagem(e.target.value)}
              rows={3}
              placeholder="Deixe em branco para usar a mensagem padrão."
              className="input w-full"
            />
          </Field>

          <button
            type="button"
            onClick={() => enviarTeste.mutate()}
            disabled={enviarTeste.isPending || testePhone.trim() === ''}
            className="rounded-lg bg-slate-800 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-slate-900 disabled:opacity-50 dark:bg-slate-700 dark:hover:bg-slate-600"
          >
            {enviarTeste.isPending ? 'Enviando…' : 'Enviar mensagem de teste'}
          </button>
        </div>
      </Section>
    </div>
  );
}

// -------------------------------------------------------------------

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mt-5 rounded-xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900">
      <h2 className="mb-3 text-sm font-semibold tracking-tight">{title}</h2>
      {children}
    </section>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="mt-3 block">
      <span className="mb-1 block text-xs font-medium text-slate-600 dark:text-slate-300">
        {label}
      </span>
      {children}
    </label>
  );
}

function Radio({
  label,
  value,
  onChange,
}: {
  label: string;
  value: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <div className="mt-3">
      <span className="mb-1 block text-xs font-medium text-slate-600 dark:text-slate-300">
        {label}
      </span>
      <div className="flex gap-4 text-sm">
        {[true, false].map((opcao) => (
          <label key={String(opcao)} className="flex cursor-pointer items-center gap-1.5">
            <input
              type="radio"
              checked={value === opcao}
              onChange={() => onChange(opcao)}
            />
            <span>{opcao ? 'Sim' : 'Não'}</span>
          </label>
        ))}
      </div>
    </div>
  );
}
